
import React, { useState, useEffect, useRef } from 'react';
import { useSeo } from '../hooks/useSeo';
import { useNavigate, useParams } from 'react-router-dom';
import { gravatarUrl } from '../components/gravatar';
import GeoSensitiveSlideshow from '../components/GeoSensitiveSlideshow';
import { BiometricAuth } from '../utils/biometric';
import { generatePreviewThumb } from '../utils/thumbnailUtils';
import { AvatarDropdown } from '../components/AvatarDropdown';
import { MobileBottomNav } from '../components/MobileBottomNav';

interface LandingProps {
  isLoggedIn: boolean;
  onLogin: (email: string) => Promise<{ verifyUrl?: string }>;
  onLogout: () => void;
}

type UploadItem = {
  file: File;
  status: 'queued' | 'uploading' | 'complete' | 'error';
  error?: string;
  fileId?: string;
};

type UploadedFile = {
  id: string;
  name: string;
  sizeBytes: number;
  mime?: string | null;
};

// Part size: 8MB keeps each Worker request well under Cloudflare's edge timeout.
// For large files more parts run in parallel so overall throughput is the same.
const PART_SIZE = 8 * 1024 * 1024;
const MAX_CONCURRENT_PARTS = 6; // sliding-window concurrency
const MAX_FILE_BYTES = 3 * 1024 * 1024 * 1024; // 3GB
const UPLOAD_STATE_KEY = 'wesaves_pending_upload';
const MOBILE_MARKETING_LINES = [
  { line1: 'Send large files securely', line2: 'in seconds.' },
  { line1: 'Store your memories', line2: 'with end-to-end protection.' },
  { line1: 'Share one private link', line2: 'not messy attachments.' },
  { line1: 'Access your files anywhere', line2: 'anytime.' },
];

const Landing: React.FC<LandingProps> = ({ isLoggedIn, onLogin, onLogout }) => {
  useSeo({
    title: 'Wesaves — Secure Cloud Storage & Digital Legacy Platform',
    description: 'Store, share and protect your most important files. End-to-end encrypted cloud storage with digital legacy planning for families and businesses.',
    canonical: '/',
  });
  const { type } = useParams<{ type: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'send' | 'store'>('send');
  const [uploadStep, setUploadStep] = useState<'idle' | 'uploading' | 'complete'>('idle');
  const [progress, setProgress] = useState(0);
  const [uploadItems, setUploadItems] = useState<UploadItem[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const [meEmail, setMeEmail] = useState<string | null>(null);
  const [planName, setPlanName] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const addMoreRef = useRef<HTMLInputElement>(null);
  const [isAddingMore, setIsAddingMore] = useState(false);

  const planLabel = planName ? `${planName} Plan` : 'Member';

  // Email/WhatsApp Form States
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSentSuccess, setEmailSentSuccess] = useState(false);
  const [emailToChips, setEmailToChips] = useState<string[]>([]);
  const [emailToInput, setEmailToInput] = useState('');
  const [emailFrom, setEmailFrom] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [mobileSlideIndex, setMobileSlideIndex] = useState(0);

  // Auth Drawer States
  const [authType, setAuthType] = useState<string | undefined>(type);

  // Restore pending upload state from sessionStorage
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(UPLOAD_STATE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setUploadedFiles(parsed);
          setUploadStep('complete');
        }
      }
    } catch {}
  }, []);
  
  useEffect(() => {
    setAuthType(type);
  }, [type]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInsideDesktopMenu = !!menuRef.current?.contains(target);
      const clickedInsideMobileMenu = !!mobileMenuRef.current?.contains(target);
      const clickedMobileMenuButton = !!mobileMenuButtonRef.current?.contains(target);

      if (!clickedInsideDesktopMenu && !clickedInsideMobileMenu && !clickedMobileMenuButton) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      setMeEmail(null);
      setPlanName('');
      return;
    }
    (async () => {
      try {
        const r = await fetch('/api/me', { credentials: 'include' });
        const j = await r.json().catch(() => ({}));
        const email = j?.user?.email ? String(j.user.email) : null;
        setMeEmail(email);
      } catch {
        setMeEmail(null);
      }

      try {
        const r = await fetch('/api/storage/usage', { credentials: 'include' });
        const j = await r.json().catch(() => ({}));
        const name = j?.plan?.name ? String(j.plan.name) : '';
        setPlanName(name);
      } catch {
        setPlanName('');
      }
    })();
  }, [isLoggedIn]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setMobileSlideIndex((prev) => (prev + 1) % MOBILE_MARKETING_LINES.length);
    }, 3200);

    return () => window.clearInterval(timer);
  }, []);

  const formatBytes = (bytes: number) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const idx = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, idx);
    return `${value.toFixed(value >= 10 || idx === 0 ? 0 : 1)} ${units[idx]}`;
  };

  const updateUploadItem = (index: number, patch: Partial<UploadItem>) => {
    setUploadItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const uploadFiles = async (items: UploadItem[], opts: { append?: boolean } = {}) => {
    const isStore = activeTab === 'store';
    const useAuthedUpload = isStore && isLoggedIn;
    let done = 0;
    const uploaded: UploadedFile[] = [];

    // Check for oversized files before starting any upload
    const oversized = items.filter((it) => it.file.size > MAX_FILE_BYTES);
    if (oversized.length > 0) {
      const names = oversized.map((it) => it.file.name).join(', ');
      setUploadError(`File${oversized.length > 1 ? 's' : ''} too large (max 3 GB): ${names}`);
      setUploadStep('idle');
      return;
    }

    for (let i = 0; i < items.length; i += 1) {
      const file = items[i].file;

      updateUploadItem(i, { status: 'uploading' });
      try {
        const createUrl = useAuthedUpload ? '/api/uploads/create' : '/api/guest/uploads/create';
        const putBase = useAuthedUpload ? '/api/uploads/part/upload' : '/api/guest/uploads/part/upload';
        const completeUrl = useAuthedUpload ? '/api/uploads/complete' : '/api/guest/uploads/complete';

        const create = await fetch(createUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: useAuthedUpload ? 'include' : undefined,
          body: JSON.stringify({ name: file.name, sizeBytes: file.size, mime: file.type }),
        }).then(async (r) => {
          const data = await r.json().catch(() => ({}));
          if (!r.ok || data?.ok === false) {
            if (data?.reason === 'storage_quota_exceeded') throw new Error('Storage quota exceeded. Upgrade your plan for more space.');
            if (data?.reason === 'file_too_large') throw new Error('File exceeds your plan\'s max file size. Upgrade for larger uploads.');
            throw new Error(data?.error || 'Failed to create upload');
          }
          return data;
        });

        // When the server returned presigned URLs, the browser sends each part
        // directly to R2 — no Worker is involved as a data pipe.
        // Otherwise fall back to the proxy path through the Worker.
        const usePresigned = Array.isArray(create.partUrls) && create.partUrls.length > 0;
        const clientPartSize: number = create.partSize || PART_SIZE;
        const totalParts = usePresigned
          ? (create.partUrls as string[]).length
          : Math.ceil(file.size / clientPartSize);
        const parts: Array<{ partNumber: number; etag: string }> = new Array(totalParts);
        let completedParts = 0;

        // Upload a single part with up to 3 retries + exponential backoff
        const uploadPart = async (partNumber: number): Promise<void> => {
          const start = (partNumber - 1) * clientPartSize;
          const chunk = file.slice(start, Math.min(start + clientPartSize, file.size));
          let lastErr: any;
          for (let attempt = 0; attempt < 3; attempt++) {
            if (attempt > 0) await new Promise(r => setTimeout(r, attempt * 1200));
            try {
              let etag: string;
              if (usePresigned) {
                // Direct PUT to R2 presigned URL — bypasses Worker entirely
                const res = await fetch((create.partUrls as string[])[partNumber - 1], {
                  method: 'PUT',
                  body: chunk,
                });
                if (!res.ok) throw new Error(`Part ${partNumber} failed (${res.status})`);
                etag = res.headers.get('ETag') || res.headers.get('etag') || '';
              } else {
                // Proxy through Worker
                const putUrl = `${putBase}?fileId=${encodeURIComponent(create.fileId)}&partNumber=${partNumber}`;
                const res = await fetch(putUrl, {
                  method: 'PUT',
                  credentials: useAuthedUpload ? 'include' : undefined,
                  body: chunk,
                });
                const data = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(data?.error || `Part ${partNumber} failed (${res.status})`);
                etag = data.etag;
              }
              parts[partNumber - 1] = { partNumber, etag };
              completedParts++;
              setProgress(Math.min(99, Math.round((completedParts / totalParts) * 100)));
              return;
            } catch (e) { lastErr = e; }
          }
          throw lastErr ?? new Error(`Part ${partNumber} failed after retries`);
        };

        // Sliding-window: keep up to MAX_CONCURRENT_PARTS in flight at all times.
        // As each part completes the next one starts immediately (no batch stall).
        await (async () => {
          let nextPart = 1;
          const inFlight = new Map<number, Promise<void>>();

          const launch = () => {
            while (inFlight.size < MAX_CONCURRENT_PARTS && nextPart <= totalParts) {
              const pn = nextPart++;
              const p = uploadPart(pn).then(() => { inFlight.delete(pn); }).catch(e => { inFlight.delete(pn); throw e; });
              inFlight.set(pn, p);
            }
          };

          launch();
          while (inFlight.size > 0) {
            await Promise.race(inFlight.values());
            launch(); // fill back up to concurrency limit
          }
        })();

        await fetch(completeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: useAuthedUpload ? 'include' : undefined,
          body: JSON.stringify({ fileId: create.fileId, parts, viaPresigned: usePresigned }),
        }).then(async (r) => {
          const data = await r.json().catch(() => ({}));
          if (!r.ok) throw new Error(data?.error || 'Failed to finalize upload');
          return data;
        });

        // Upload thumbnail for images (best-effort)
        try {
          if ((file.type || '').startsWith('image/')) {
            const imgUrl = URL.createObjectURL(file);
            const img = new Image();
            img.src = imgUrl;
            await new Promise<void>((resolve, reject) => {
              img.onload = () => resolve();
              img.onerror = () => reject(new Error('thumb load failed'));
            });

            const maxW = 400;
            const scale = Math.min(1, maxW / (img.width || maxW));
            const w = Math.max(1, Math.round((img.width || maxW) * scale));
            const h = Math.max(1, Math.round((img.height || maxW) * scale));

            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(img, 0, 0, w, h);
              const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', 0.8));
              if (blob) {
                const thumbUrl = useAuthedUpload
                  ? `/api/uploads/thumb/upload?fileId=${encodeURIComponent(create.fileId)}`
                  : `/api/guest/uploads/thumb/upload?fileId=${encodeURIComponent(create.fileId)}`;
                await fetch(thumbUrl, {
                  method: 'PUT',
                  credentials: useAuthedUpload ? 'include' : undefined,
                  headers: { 'Content-Type': 'image/webp' },
                  body: blob,
                }).catch(() => null);
              }
            }
            // Fire-and-forget 1600px preview thumb for logged-in users
            if (useAuthedUpload) {
              void generatePreviewThumb(create.fileId, imgUrl).finally(() => URL.revokeObjectURL(imgUrl));
            } else {
              URL.revokeObjectURL(imgUrl);
            }
          }
        } catch {
          // ignore thumb errors
        }

        updateUploadItem(i, { status: 'complete', fileId: create.fileId });
        uploaded.push({ id: create.fileId, name: file.name, sizeBytes: file.size, mime: file.type || null });
      } catch (err: any) {
        updateUploadItem(i, { status: 'error', error: String(err?.message || err) });
      } finally {
        done += 1;
        setProgress(Math.round((done / items.length) * 100));
      }
    }

    // Append to existing files if called from "add more", otherwise replace
    if (opts.append) {
      setUploadedFiles(prev => {
        const merged = [...prev, ...uploaded];
        try { sessionStorage.setItem(UPLOAD_STATE_KEY, JSON.stringify(merged)); } catch {}
        return merged;
      });
      setIsAddingMore(false);
      if (addMoreRef.current) addMoreRef.current.value = '';
    } else {
    setUploadedFiles(uploaded);
    if (uploaded.length) {
      setUploadStep('complete');
        try { sessionStorage.setItem(UPLOAD_STATE_KEY, JSON.stringify(uploaded)); } catch {}
    } else {
      setUploadStep('idle');
      setUploadError('Upload failed. Please try again.');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFilesSelected = (files: FileList | File[]) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploadError(null);
    setEmailError(null);
    setEmailSentSuccess(false);
    setDownloadUrl(null);
    setUploadedFiles([]); try { sessionStorage.removeItem(UPLOAD_STATE_KEY); } catch {}
    setProgress(0);
    const items = list.map((file) => ({ file, status: 'queued' as const }));
    setUploadItems(items);
    setUploadStep('uploading');
    void uploadFiles(items);
  };

  const triggerFilePick = () => {
    if (uploadStep !== 'idle') return;

    // Store requires an account.
    if (activeTab === 'store' && !isLoggedIn) {
      navigate('/auth/signup');
      return;
    }

    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    handleFilesSelected(e.target.files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (uploadStep !== 'idle') return;
    // Store requires signup
    if (activeTab === 'store' && !isLoggedIn) return;
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (uploadStep !== 'idle') return;

    if (activeTab === 'store' && !isLoggedIn) {
      navigate('/auth/signup');
      return;
    }

    handleFilesSelected(e.dataTransfer.files);
  };

  const handleAddMoreFiles = (files: FileList | File[]) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const items = list.map((file) => ({ file, status: 'queued' as const }));
    // Append new items to the visible list so user sees them uploading
    setUploadItems(prev => [...prev, ...items]);
    setIsAddingMore(true);
    void uploadFiles(items, { append: true });
  };

  const [shareToken, setShareToken] = useState<string | null>(null);

  const ensureShareLink = async () => {
    if (downloadUrl && shareToken) return { downloadUrl, shareToken };
    if (!uploadedFiles.length) return null;

    setIsSendingEmail(true);
    setEmailError(null);
    try {
      const requestBody = {
        senderEmail: emailFrom || null,
        recipientEmail: null,
        message: emailMessage,
        fileIds: uploadedFiles.map((f) => f.id),
      };
      
      const res = await fetch('/api/transfers/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      });
      
      const data = await res.json().catch(() => ({}));
      
      if (!res.ok || data?.ok === false) {
        console.error('transfers/create failed:', data);
        throw new Error(data?.message || data?.error || 'Failed to create link');
      }
      
      const tok = String(data.publicId || data.public_id || '');
      const url = String(data.downloadUrl || '');
      
      setShareToken(tok);
      setDownloadUrl(url);
      return { shareToken: tok, downloadUrl: url };
    } catch (err: any) {
      setEmailError(String(err?.message || err));
      return null;
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Confirm any pending email chip before submitting
  const confirmEmailChip = () => {
    const val = emailToInput.trim().toLowerCase();
    if (val && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) && !emailToChips.includes(val)) {
      setEmailToChips((prev) => [...prev, val]);
      setEmailToInput('');
      return true;
    }
    setEmailToInput('');
    return false;
  };

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    // Confirm any unfinished chip in the input box
    const pending = emailToInput.trim().toLowerCase();
    const allRecipients = [...emailToChips];
    if (pending && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pending) && !allRecipients.includes(pending)) {
      allRecipients.push(pending);
    }
    if (!allRecipients.length || !emailFrom || !uploadedFiles.length) return;

    // Ensure we have a share link first
    const ensured = await ensureShareLink();
    if (!ensured) return;
    const token = ensured.shareToken;
    const url = ensured.downloadUrl;

    setIsSendingEmail(true);
    setEmailError(null);
    try {
      let lastData: any = null;
      for (const recipient of allRecipients) {
        const res = await fetch('/api/transfers/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token,
            senderEmail: emailFrom,
            recipientEmail: recipient,
            message: emailMessage,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.ok === false) {
          throw new Error(data?.error || 'Failed to send email');
        }
        lastData = data;
      }
      setEmailSentSuccess(true);
      setDownloadUrl(lastData?.downloadUrl || url);
    } catch (err: any) {
      setEmailError(String(err?.message || err));
    } finally {
      setIsSendingEmail(false);
    }
  };

  const getWhatsAppShareUrl = (link: string) => {
    const text = encodeURIComponent(`I've shared some files with you via Wesaves: ${link}`);
    return `https://wa.me/?text=${text}`;
  };

  const openWhatsApp = (link: string) => {
    const text = encodeURIComponent(`I've shared some files with you via Wesaves: ${link}`);
    
    // Try to open WhatsApp app first (custom URL scheme)
    const appUrl = `whatsapp://send?text=${text}`;
    const webUrl = `https://wa.me/?text=${text}`;
    
    // Create a hidden link element to test if app is available
    const testLink = document.createElement('a');
    testLink.href = appUrl;
    
    // Try opening app first
    try {
      window.location.href = appUrl;
      
      // Fallback to web if app doesn't open (setTimeout to give app time to launch)
      setTimeout(() => {
        window.open(webUrl, '_blank');
      }, 2000);
    } catch (error) {
      // If direct assignment fails, open web version
      window.open(webUrl, '_blank');
    }
  };

  const handleWhatsAppShare = async () => {
    try {
      const ensured = await ensureShareLink();
      if (!ensured?.downloadUrl) {
        return;
      }
      openWhatsApp(ensured.downloadUrl);
    } catch (error) {
    }
  };

  const resetForm = () => {
    setUploadStep('idle');
    setProgress(0);
    setUploadItems([]);
    setUploadedFiles([]); try { sessionStorage.removeItem(UPLOAD_STATE_KEY); } catch {}
    setUploadError(null);
    setEmailError(null);
    setEmailSentSuccess(false);
    setEmailTo('');
    setEmailFrom('');
    setEmailMessage('');
    setDownloadUrl(null);
  };

  const closeAuth = () => navigate('/');

  const toggleAuthType = () => {
    const nextType = authType === 'signin' ? 'signup' : 'signin';
    setAuthError(null);
    setAuthSent(false);
    setShow2FA(false);
    setPending2FAToken(null);
    setTwoFACode('');
    setAuthPassword('');
    setAuthName('');
    // Signup always uses password mode
    if (nextType === 'signup') setAuthMode('password');
    navigate(`/auth/${nextType}`);
  };

  const handleStorageClick = () => {
    if (isLoggedIn) navigate('/drive');
    else navigate('/storage');
  };

  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authName, setAuthName] = useState('');
  const [authSending, setAuthSending] = useState(false);
  const [authSent, setAuthSent] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authMode, setAuthMode] = useState<'magic' | 'password'>('magic'); // default to email link
  const [showPassword, setShowPassword] = useState(false);

  const [devVerifyUrl, setDevVerifyUrl] = useState<string | null>(null);

  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [pending2FAToken, setPending2FAToken] = useState<string | null>(null);
  const [twoFACode, setTwoFACode] = useState('');

  // Biometric auth states
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricType, setBiometricType] = useState('Biometric');
  const [biometricAuthenticating, setBiometricAuthenticating] = useState(false);

  // Check biometric availability
  useEffect(() => {
    const checkBiometric = async () => {
      const available = await BiometricAuth.isAvailable();
      setBiometricAvailable(available);
      if (available) {
        const type = await BiometricAuth.getBiometricType();
        setBiometricType(type);
      }
    };
    checkBiometric();
  }, []);

  const handleBiometricSignIn = async () => {
    setBiometricAuthenticating(true);
    setAuthError(null);
    try {
      const result = await BiometricAuth.authenticate();
      if (result.success) {
        window.location.href = '/#/dashboard';
        window.location.reload();
      } else {
        setAuthError('Biometric authentication failed. Try another method.');
      }
    } catch (error: any) {
      setAuthError(error.message || 'Biometric authentication failed');
    } finally {
      setBiometricAuthenticating(false);
    }
  };

  // Magic link submit (existing flow)
  const handleMagicLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!authEmail) return;
    setAuthSending(true);
    setAuthSent(false);
    setDevVerifyUrl(null);
    setAuthError(null);
    try {
      const { verifyUrl } = await onLogin(authEmail);
      setAuthSent(true);
      if (verifyUrl) {
        setDevVerifyUrl(verifyUrl);
      } else {
        closeAuth();
      }
    } catch (err: any) {
      setAuthError(err.message || 'Failed to send magic link');
    } finally {
      setAuthSending(false);
    }
  };

  // Password login/signup submit
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!authEmail || !authPassword) return;
    setAuthSending(true);
    setAuthError(null);
    try {
      const isSignup = authType === 'signup';
      const endpoint = isSignup ? '/api/auth/signup' : '/api/auth/login';
      const payload: any = { email: authEmail, password: authPassword };
      if (isSignup && authName) payload.displayName = authName;

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.ok) {
        setAuthError(data.error || 'Authentication failed');
        return;
      }

      if (data.requires2FA) {
        setPending2FAToken(data.pendingToken);
        setShow2FA(true);
        return;
      }

      // Success — session cookie is set
      window.location.href = '/#/dashboard';
      window.location.reload();
    } catch (err: any) {
      setAuthError(err.message || 'Network error. Please try again.');
    } finally {
      setAuthSending(false);
    }
  };

  // 2FA code submit
  const handle2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!twoFACode || twoFACode.length < 6) return;
    setAuthSending(true);
    setAuthError(null);
    try {
      const res = await fetch('/api/auth/2fa/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: twoFACode, pendingToken: pending2FAToken }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.ok) {
        setAuthError(data.error || 'Invalid verification code');
        return;
      }

      window.location.href = '/#/dashboard';
      window.location.reload();
    } catch (err: any) {
      setAuthError(err.message || 'Verification failed');
    } finally {
      setAuthSending(false);
    }
  };

  return (
    <div className="relative flex h-screen w-full flex-col overflow-hidden bg-background-light font-sans">
      
      {/* Auth Drawer Component */}
      {authType && (
        <div className="absolute inset-0 z-[100] flex justify-end">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-500" onClick={closeAuth}></div>
          <div className="relative h-full w-full sm:max-w-[480px] bg-white shadow-2xl flex flex-col border-l border-slate-100 animate-slide-in">
            <div className="flex items-center justify-between p-6 pb-2">
              <button onClick={closeAuth} className="flex items-center gap-2 text-sm font-bold text-slate-400 hover:text-primary transition-colors group">
                <span className="material-symbols-outlined text-[20px] group-hover:-translate-x-1 transition-transform">arrow_back</span>
                Back to Home
              </button>
              <button onClick={closeAuth} className="p-2 text-slate-400 hover:bg-slate-100 rounded-full transition-colors"><span className="material-symbols-outlined">close</span></button>
            </div>
            <div className="flex-1 px-8 py-4 sm:px-12 flex flex-col justify-center">
              <div className="mb-8">
                <h1 className="text-3xl font-black text-slate-900 tracking-tight leading-tight mb-3">{authType === 'signin' ? 'Welcome Back' : 'Create Account'}</h1>
                <p className="text-slate-500 font-medium leading-relaxed">Securely manage your data with Wesaves intelligent cloud.</p>
              </div>
              {/* ── 2FA Verification Screen ── */}
              {show2FA ? (
                <form className="space-y-5" onSubmit={handle2FASubmit}>
                  <div className="flex items-center gap-3 mb-2">
                    <button type="button" onClick={() => { setShow2FA(false); setPending2FAToken(null); setTwoFACode(''); setAuthError(null); }} className="p-1.5 -ml-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                      <span className="material-symbols-outlined text-slate-400 text-xl">arrow_back</span>
                    </button>
                    <h2 className="text-lg font-black text-slate-900">Two-Factor Authentication</h2>
                  </div>

                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-start gap-3">
                    <span className="material-symbols-outlined text-blue-500 text-xl mt-0.5">security</span>
                    <p className="text-sm text-blue-700 font-medium">Enter the 6-digit code from your authenticator app, or use a backup code.</p>
                  </div>

                  {authError && (
                    <div className="bg-red-50 border border-red-100 text-red-700 rounded-xl p-3 text-sm font-semibold flex items-center gap-2">
                      <span className="material-symbols-outlined text-red-500 text-lg">error</span>
                      {authError}
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-black uppercase text-slate-400 tracking-widest mb-2">Verification Code</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      required
                      maxLength={44}
                      value={twoFACode}
                      onChange={(e) => setTwoFACode(e.target.value.replace(/\s/g, ''))}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl h-14 px-4 focus:ring-2 focus:ring-primary/20 text-center text-2xl font-black tracking-[0.3em] placeholder:text-base placeholder:tracking-normal"
                      placeholder="000000"
                      autoFocus
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={authSending || twoFACode.length < 6}
                    className="w-full bg-primary hover:bg-blue-600 disabled:opacity-60 text-white font-black h-12 rounded-xl shadow-lg shadow-blue-500/20 transition-all active:scale-[0.98]"
                  >
                    {authSending ? 'Verifying…' : 'Verify & Sign In'}
                  </button>
                </form>
              ) : (
                /* ── Main Auth Form ── */
                <form className="space-y-5" onSubmit={authMode === 'password' ? handlePasswordSubmit : handleMagicLinkSubmit}>

                  {/* Auth mode toggle – only show on sign-in */}
                  {authType === 'signin' && (
                    <div className="flex bg-slate-100 rounded-xl p-1 gap-1">
                      <button
                        type="button"
                        onClick={() => { setAuthMode('magic'); setAuthError(null); }}
                        className={`flex-1 h-9 rounded-lg text-xs font-black uppercase tracking-wider transition-all ${authMode === 'magic' ? 'bg-white text-primary shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                      >
                        Email Link
                      </button>
                      <button
                        type="button"
                        onClick={() => { setAuthMode('password'); setAuthError(null); }}
                        className={`flex-1 h-9 rounded-lg text-xs font-black uppercase tracking-wider transition-all ${authMode === 'password' ? 'bg-white text-primary shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                      >
                        Password
                      </button>
                    </div>
                  )}

                  {/* Error display */}
                  {authError && (
                    <div className="bg-red-50 border border-red-100 text-red-700 rounded-xl p-3 text-sm font-semibold flex items-center gap-2 animate-in fade-in duration-200">
                      <span className="material-symbols-outlined text-red-500 text-lg">error</span>
                      {authError}
                    </div>
                  )}

                  {/* Name field – signup only, password mode */}
                  {authType === 'signup' && (
                    <div className="animate-in fade-in duration-300">
                      <label className="block text-xs font-black uppercase text-slate-400 tracking-widest mb-2">Full Name</label>
                      <input
                        type="text"
                        value={authName}
                        onChange={(e) => setAuthName(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl h-12 px-4 focus:ring-2 focus:ring-primary/20 text-sm font-medium"
                        placeholder="John Doe"
                      />
                    </div>
                  )}

                  {/* Email field */}
                <div className="animate-in fade-in duration-300">
                  <label className="block text-xs font-black uppercase text-slate-400 tracking-widest mb-2">Email Address</label>
                  <input
                    type="email"
                    required
                    value={authEmail}
                    onChange={(e) => setAuthEmail(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl h-12 px-4 focus:ring-2 focus:ring-primary/20 text-sm font-medium"
                    placeholder="name@company.com"
                  />
                </div>

                  {/* Password field – only for password mode OR signup */}
                  {(authMode === 'password' || authType === 'signup') && (
                    <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                      <label className="block text-xs font-black uppercase text-slate-400 tracking-widest mb-2">Password</label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          required
                          minLength={8}
                          value={authPassword}
                          onChange={(e) => setAuthPassword(e.target.value)}
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl h-12 px-4 pr-12 focus:ring-2 focus:ring-primary/20 text-sm font-medium"
                          placeholder={authType === 'signup' ? 'Min 8 characters' : '••••••••'}
                        />
                        <button
                          type="button"
                          tabIndex={-1}
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 transition-colors"
                        >
                          <span className="material-symbols-outlined text-xl">{showPassword ? 'visibility_off' : 'visibility'}</span>
                        </button>
                      </div>
                      {authType === 'signup' && authPassword.length > 0 && authPassword.length < 8 && (
                        <p className="text-xs text-amber-500 font-medium mt-1.5">Password must be at least 8 characters</p>
                      )}
                    </div>
                  )}

                  {/* Magic link sent success */}
                  {authSent && authMode === 'magic' && (
                    <div className="bg-green-50 border border-green-100 text-green-700 rounded-xl p-3 text-sm font-semibold animate-in fade-in duration-200">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="material-symbols-outlined text-green-500 text-lg">mark_email_read</span>
                        Magic link sent! Check your email.
                      </div>
                    {devVerifyUrl && (
                      <div className="mt-2 text-xs font-bold">
                          Dev link: <a className="underline text-green-600" href={devVerifyUrl}>Click to sign in</a>
                      </div>
                    )}
                  </div>
                )}

                  {/* Submit button */}
                <button
                  type="submit"
                  disabled={authSending}
                    className="w-full bg-primary hover:bg-blue-600 disabled:opacity-60 text-white font-black h-12 rounded-xl shadow-lg shadow-blue-500/20 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                  >
                    {authSending ? (
                      <>
                        <div className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>{authMode === 'magic' ? 'Sending link…' : (authType === 'signup' ? 'Creating account…' : 'Signing in…')}</span>
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined text-xl">
                          {authMode === 'magic' ? 'mail' : (authType === 'signup' ? 'person_add' : 'login')}
                        </span>
                        <span>
                          {authMode === 'magic'
                            ? (authType === 'signin' ? 'Send Sign-in Link' : 'Send Sign-up Link')
                            : (authType === 'signup' ? 'Create Account' : 'Sign In')}
                        </span>
                      </>
                    )}
                </button>

                  {/* Biometric Sign-In Option */}
                  {authType === 'signin' && biometricAvailable && (
                    <>
                      <div className="relative my-2">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-slate-200"></div>
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-white px-3 text-slate-400 font-bold">or</span>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={handleBiometricSignIn}
                        disabled={biometricAuthenticating}
                        className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:opacity-60 text-white font-black h-12 rounded-xl shadow-lg shadow-purple-500/20 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                      >
                        {biometricAuthenticating ? (
                          <>
                            <div className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            <span>Authenticating…</span>
                          </>
                        ) : (
                          <>
                            <span className="material-symbols-outlined text-xl">
                              {biometricType.includes('Face') ? 'face' : biometricType.includes('Fingerprint') ? 'fingerprint' : 'shield'}
                            </span>
                            <span>Sign in with {biometricType}</span>
                          </>
                        )}
                      </button>
                    </>
                  )}
              </form>
              )}
              <div className="mt-10 text-center"><p className="text-sm font-medium text-slate-500">{authType === 'signin' ? "No account?" : "Already member?"}<button onClick={toggleAuthType} className="text-primary font-black hover:underline ml-2">{authType === 'signin' ? 'Sign Up' : 'Log In'}</button></p></div>
            </div>
          </div>
        </div>
      )}

      {/* Main Header */}
      <header className="flex items-center justify-between whitespace-nowrap px-6 py-4 md:px-10 absolute top-0 left-0 right-0 z-50">
        {/* Desktop Logo */}
        <div className="hidden md:flex items-center gap-3 text-[#0d121b] cursor-pointer group" onClick={() => navigate('/')}>
          <img
            src="/brand/wesaves-logo.png"
            alt="Wesaves"
            className="h-7 md:h-8 w-auto select-none invert drop-shadow-lg"
            draggable={false}
          />
        </div>

        {/* Mobile Header - Logged In */}
        {isLoggedIn && (
          <div className="md:hidden flex items-center gap-3 flex-1">
            <button 
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="size-10 -ml-1 rounded-full bg-white/95 backdrop-blur-sm hover:bg-white transition-colors shadow-sm flex items-center justify-center"
            >
              <span className="material-symbols-outlined text-2xl text-slate-700">menu</span>
            </button>
            
            <div className="flex items-center">
              <img src="/brand/wesaves-logo.png" alt="Wesaves" className="h-6 w-auto invert drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)]" onClick={() => navigate('/')} />
            </div>
          </div>
        )}

        {/* Mobile Logo - Not Logged In */}
        {!isLoggedIn && (
          <div className="md:hidden flex items-center gap-3 text-[#0d121b] cursor-pointer group" onClick={() => navigate('/')}>
            <img
              src="/brand/wesaves-logo.png"
              alt="Wesaves"
              className="h-7 w-auto select-none invert drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)]"
              draggable={false}
            />
          </div>
        )}

        <div className="hidden md:flex flex-1 justify-end gap-4 items-center">
          <nav className="flex items-center gap-8 bg-white rounded-full px-8 py-3 shadow-lg shadow-black/5">
            {isLoggedIn && (
              <>
                <button onClick={() => navigate('/dashboard')} className="text-sm font-bold text-slate-500 hover:text-primary transition-colors">
                  Home
                </button>
                <button onClick={() => navigate('/drive')} className="text-sm font-bold text-slate-500 hover:text-primary transition-colors">
                  My Drive
                </button>
              </>
            )}
            <button onClick={() => navigate('/whats-new')} className="text-gray-900 hover:text-primary transition-colors text-sm font-bold">What's New</button>
            <button onClick={() => navigate('/pricing')} className="text-gray-900 hover:text-primary transition-colors text-sm font-bold">Pricing</button>
          </nav>
          <div className="flex items-center gap-2 bg-white rounded-full p-1.5 pl-6 shadow-lg shadow-black/5 relative">
            <AvatarDropdown isLoggedIn={isLoggedIn} onLogout={onLogout} />
                   </div>
                </div>

        {/* Mobile Right Actions */}
        <div className="md:hidden flex items-center gap-2">
          {isLoggedIn ? (
            <AvatarDropdown isLoggedIn={isLoggedIn} onLogout={onLogout} />
          ) : (
        <button 
              ref={mobileMenuButtonRef}
              onClick={() => setShowUserMenu(prev => !prev)} 
              className="flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-lg shadow-black/5 hover:bg-slate-50 transition-colors"
        >
          <span className="material-symbols-outlined text-slate-700">
            {showUserMenu ? 'close' : 'menu'}
          </span>
        </button>
          )}
        </div>
      </header>

      {/* Mobile Menu Dropdown */}
      {showUserMenu && (
        <div
          ref={mobileMenuRef}
          className="md:hidden fixed top-16 right-0 left-0 bg-white rounded-xl shadow-2xl border border-slate-100 overflow-hidden z-[110] mx-6 animate-in fade-in zoom-in-95 duration-200"
        >
          {/* Navigation Links */}
          <div className="p-4 border-b border-slate-100">
            {isLoggedIn && (
              <>
                <button onClick={() => { setShowUserMenu(false); navigate('/dashboard'); }} className="w-full text-left px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 rounded-lg transition-colors">
                  <span className="material-symbols-outlined text-lg mr-2">dashboard</span>
                  Dashboard
                </button>
                <button onClick={() => { setShowUserMenu(false); navigate('/drive'); }} className="w-full text-left px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 rounded-lg transition-colors">
                  <span className="material-symbols-outlined text-lg mr-2">hard_drive</span>
                  My Drive
                </button>
              </>
            )}
            <button onClick={() => { setShowUserMenu(false); navigate('/whats-new'); }} className="w-full text-left px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 rounded-lg transition-colors">
              What's New
            </button>
            <button onClick={() => { setShowUserMenu(false); navigate('/pricing'); }} className="w-full text-left px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 rounded-lg transition-colors">
              Pricing
            </button>
            {!isLoggedIn && (
              <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                <button
                  onClick={() => { setShowUserMenu(false); navigate('/auth/signin'); }}
                  className="w-full text-left px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                >
                  Log In
                </button>
                <button
                  onClick={() => { setShowUserMenu(false); navigate('/auth/signup'); }}
                  className="w-full flex items-center justify-center rounded-xl h-11 bg-primary hover:bg-blue-600 transition-colors text-white text-sm font-bold"
                >
                  Get Storage
                </button>
              </div>
            )}
            {isLoggedIn && (
              <>
                <button onClick={() => { setShowUserMenu(false); navigate('/profile'); }} className="w-full text-left px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 rounded-lg transition-colors">
                  <span className="material-symbols-outlined text-lg mr-2">account_circle</span>
                  Profile
                </button>
                <button onClick={() => { setShowUserMenu(false); navigate('/billing'); }} className="w-full text-left px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 rounded-lg transition-colors">
                  <span className="material-symbols-outlined text-lg mr-2">payments</span>
                  Billing
                </button>
                <button onClick={() => { setShowUserMenu(false); navigate('/settings'); }} className="w-full text-left px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 rounded-lg transition-colors">
                  <span className="material-symbols-outlined text-lg mr-2">settings</span>
                  Settings
                </button>
              </>
            )}
          </div>

          {isLoggedIn && (
            <div className="p-2 border-t border-slate-50">
              <button onClick={(e) => { 
                e.preventDefault(); 
                e.stopPropagation(); 
                setShowUserMenu(false); 
                setTimeout(() => onLogout(), 100); 
              }} className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold text-red-500 hover:bg-red-50 transition-all text-left">
                <span className="material-symbols-outlined text-lg">logout</span>
                Sign Out
              </button>
            </div>
          )}
        </div>
      )}

      {/* Main Landing Panel */}
      <main className="flex-1 flex flex-col md:flex-row h-full relative">
        {/* Slideshow Background - Full Width */}
        <div className="absolute inset-0 z-0">
          <GeoSensitiveSlideshow 
            apiKey="G9UPa4SXNnv2eirQ1c6s9W81xm345WagePkhJx2Jzy8"
            interval={10000}
          />
        </div>
        
        <div className="w-full md:w-[350px] flex-shrink-0 bg-white/88 md:bg-transparent rounded-b-[28px] md:rounded-none backdrop-blur-[2px] shadow-xl z-20 flex flex-col px-4 md:px-6 pt-24 pb-10 md:pb-8 overflow-visible md:overflow-hidden relative">
          {/* Very light gradient overlay (desktop only) */}
          <div className="hidden md:block absolute inset-0 bg-gradient-to-r from-white/5 via-white/2 to-transparent pointer-events-none z-0"></div>
          
          <div className="md:hidden relative z-10 mb-3">
            <div key={mobileSlideIndex} className="font-serif text-white drop-shadow animate-in fade-in slide-in-from-bottom-1 duration-500">
              <p className="text-[17px] font-medium tracking-tight leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
                {MOBILE_MARKETING_LINES[mobileSlideIndex].line1}
              </p>
              <p className="text-sm font-medium tracking-tight leading-tight text-white/90 whitespace-nowrap overflow-hidden text-ellipsis mt-0.5">
                {MOBILE_MARKETING_LINES[mobileSlideIndex].line2}
              </p>
            </div>
            <div className="mt-2 flex items-center gap-1.5">
              {MOBILE_MARKETING_LINES.map((_, idx) => (
                <span
                  key={idx}
                  className={`h-1 rounded-full transition-all duration-300 ${idx === mobileSlideIndex ? 'w-5 bg-white' : 'w-1.5 bg-white/60'}`}
                />
              ))}
            </div>
          </div>

          <div className={`bg-gray-100 p-1 flex items-center relative z-10 transition-all ${uploadStep === 'idle' ? 'mb-0 rounded-t-xl rounded-b-none border border-slate-200 border-b-0 md:mb-6 md:rounded-xl md:border-0' : 'mb-6 rounded-xl'}`}>
            <div className={`absolute top-1 bottom-1 w-[calc(50%-5px)] bg-black rounded-lg transition-all duration-300 z-0 ${activeTab === 'send' ? 'left-1' : 'left-[50%]'}`}></div>
            <button onClick={() => { setActiveTab('send'); resetForm(); }} className={`relative z-10 flex-1 py-2 text-xs font-black transition-colors ${activeTab === 'send' ? 'text-white' : 'text-gray-500'}`}>Send files</button>
            <button onClick={() => { setActiveTab('store'); resetForm(); }} className={`relative z-10 flex-1 py-2 text-xs font-black transition-colors ${activeTab === 'store' ? 'text-white' : 'text-gray-500'}`}>Store files</button>
          </div>

          <div className="relative z-10 md:flex-1 bg-transparent">
            {uploadError && (
              <div className="mb-4 bg-red-50 border border-red-100 text-red-600 rounded-xl p-3 text-sm font-semibold">
                {uploadError}
              </div>
            )}
            {uploadStep === 'idle' && (
              <div
                onClick={triggerFilePick}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`min-h-[340px] md:min-h-[600px] border-2 border-dashed border-t-0 md:border-t-2 rounded-b-[28px] rounded-t-none md:rounded-2xl flex flex-col items-center justify-center cursor-pointer transition-all animate-in zoom-in-95 duration-300 mb-6 md:mb-0 ${activeTab === 'send' ? 'bg-blue-50 border-blue-200 hover:bg-blue-50' : 'bg-slate-50 border-slate-200 hover:bg-slate-100'} ${isDragging ? 'ring-2 ring-primary/30 border-primary' : ''}`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileInputChange}
                />
                <div className={`size-16 rounded-2xl flex items-center justify-center mb-4 shadow-lg ${activeTab === 'send' ? 'bg-primary text-white' : 'bg-slate-900 text-white'}`}>
                  <span className="material-symbols-outlined text-3xl">{activeTab === 'send' ? 'upload_file' : 'add_to_drive'}</span>
                </div>
                <h3 className="text-sm font-black text-slate-900 text-center px-6 leading-tight">Click to browse or drop files here</h3>
                <p className="text-xs text-slate-400 mt-3 font-bold uppercase tracking-widest">Up to 3GB per file</p>
              </div>
            )}

            {uploadStep === 'uploading' && (
              <div className="min-h-[240px] md:min-h-[300px] flex flex-col items-center justify-center animate-in fade-in duration-300 bg-slate-50 rounded-3xl p-4 md:p-5 border border-slate-100">
                <div className="relative size-32 mb-6">
                   <svg className="size-full -rotate-90" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="45" fill="none" className="stroke-slate-100" strokeWidth="8"></circle>
                      <circle cx="50" cy="50" r="45" fill="none" className="stroke-primary" strokeWidth="8" strokeDasharray="283" strokeDashoffset={283 - (283 * progress) / 100} strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.3s ease' }}></circle>
                   </svg>
                   <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-2xl font-black text-slate-900">{progress}%</span>
                      <span className="text-[9px] font-black uppercase text-slate-400 tracking-widest">Uploading</span>
                   </div>
                </div>
                <div className="text-center">
                  <h3 className="text-sm font-black text-slate-900 mb-1">Syncing with Wesaves Cloud</h3>
                  <p className="text-xs text-slate-500 font-medium animate-pulse">Establishing encrypted high-speed tunnel...</p>
                </div>
                {uploadItems.length > 0 && (
                  <div className="w-full max-w-sm mt-8 space-y-2 max-h-[40vh] overflow-y-auto md:max-h-[50vh]">
                    {uploadItems.map((item, idx) => (
                      <div key={`${item.file.name}-${idx}`} className="flex items-center justify-between bg-white rounded-xl border border-slate-100 px-4 py-2 text-xs font-bold">
                        <span className="truncate max-w-[180px] text-slate-700">{item.file.name}</span>
                        <span className={`uppercase tracking-widest ${item.status === 'complete' ? 'text-green-600' : item.status === 'error' ? 'text-red-500' : 'text-slate-400'}`}>
                          {item.status === 'uploading' ? 'Uploading' : item.status === 'complete' ? 'Done' : item.status === 'error' ? 'Error' : 'Queued'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {uploadStep === 'complete' && activeTab === 'send' && !emailSentSuccess && (
              <div className="animate-in slide-in-from-bottom-8 duration-500 space-y-4 bg-slate-50 rounded-3xl p-4 md:p-5 border border-slate-100">
                 <input
                    ref={addMoreRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={e => { if (e.target.files?.length) handleAddMoreFiles(e.target.files); }}
                 />
                 <div className="bg-blue-50 rounded-2xl p-4 border border-blue-100 mb-3 flex items-center gap-3">
                    <div className="size-10 bg-white rounded-lg flex items-center justify-center text-primary shadow-sm shrink-0"><span className="material-symbols-outlined text-2xl">done_all</span></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-black text-slate-900">{uploadedFiles.length} File{uploadedFiles.length === 1 ? '' : 's'} Ready</p>
                      <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">
                        {formatBytes(uploadedFiles.reduce((sum, f) => sum + (f.sizeBytes || 0), 0))} • Encrypted
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => addMoreRef.current?.click()}
                      disabled={isAddingMore}
                      className="shrink-0 flex items-center gap-1 text-[10px] font-black text-primary uppercase tracking-widest hover:bg-blue-100 px-2 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {isAddingMore ? (
                        <div className="size-3 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                      ) : (
                        <span className="material-symbols-outlined text-sm">add</span>
                      )}
                      {isAddingMore ? 'Adding…' : 'Add files'}
                    </button>
                 </div>

                 <form onSubmit={handleSendEmail} className="space-y-3">
                    <div className="space-y-1">
                       <label className="text-[9px] font-black uppercase text-slate-400 tracking-widest ml-1">Email to</label>
                       {/* Chip-style multi-email input */}
                       <div
                         className="flex flex-wrap gap-1 min-h-[40px] bg-white border border-slate-200 rounded-lg px-2 py-1.5 transition-all cursor-text"
                         onClick={(e) => (e.currentTarget.querySelector('input') as HTMLInputElement | null)?.focus()}
                       >
                         {emailToChips.map((chip) => (
                           <span key={chip} className="inline-flex items-center gap-1 bg-primary/10 text-primary text-[11px] font-bold rounded-full px-2 py-0.5">
                             {chip}
                             <button type="button" className="hover:text-red-500 leading-none" onClick={() => setEmailToChips((p) => p.filter((c) => c !== chip))}>×</button>
                           </span>
                         ))}
                         <input
                           type="text"
                           value={emailToInput}
                           onChange={(e) => setEmailToInput(e.target.value)}
                           onKeyDown={(e) => {
                             if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
                               e.preventDefault();
                               confirmEmailChip();
                             } else if (e.key === 'Backspace' && !emailToInput) {
                               setEmailToChips((p) => p.slice(0, -1));
                             }
                           }}
                           onBlur={confirmEmailChip}
                           placeholder={emailToChips.length === 0 ? 'recipient@email.com, another@email.com…' : ''}
                           className="flex-1 min-w-[140px] text-xs font-bold bg-transparent outline-none py-0.5"
                         />
                       </div>
                       <p className="text-[10px] text-slate-400 ml-1">Press Enter or comma to add multiple recipients</p>
                    </div>
                    <div className="space-y-1">
                       <label className="text-[9px] font-black uppercase text-slate-400 tracking-widest ml-1">Your email</label>
                       <input type="email" required value={emailFrom} onChange={e => setEmailFrom(e.target.value)} placeholder="your@email.com" className="w-full h-10 bg-white border border-slate-200 rounded-lg px-3 text-xs font-bold focus:ring-2 focus:ring-primary/20 transition-all" />
                    </div>
                    <div className="space-y-1">
                       <label className="text-[9px] font-black uppercase text-slate-400 tracking-widest ml-1">Message</label>
                       <textarea rows={2} value={emailMessage} onChange={e => setEmailMessage(e.target.value)} placeholder="Add a message (optional)..." className="w-full bg-white border border-slate-200 rounded-lg p-3 text-xs font-bold focus:ring-2 focus:ring-primary/20 transition-all resize-none" />
                    </div>
                    {emailError && (
                      <div className="bg-red-50 border border-red-100 text-red-600 rounded-xl p-3 text-sm font-semibold">
                        {emailError}
                      </div>
                    )}

                    <div className="pt-3 flex flex-col gap-2 pb-2">
                       <button type="submit" disabled={isSendingEmail || !uploadedFiles.length || (emailToChips.length === 0 && !emailToInput.trim()) || !emailFrom} className="w-full h-12 bg-primary hover:bg-blue-600 text-white font-black rounded-lg shadow-lg shadow-blue-500/20 transition-all flex items-center justify-center gap-2 text-sm disabled:opacity-60">
                          {isSendingEmail ? (
                            <><div className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>Sending...</>
                          ) : (
                            <><span className="material-symbols-outlined text-lg">send</span>Send Email</>
                          )}
                       </button>

                       <button
                         type="button"
                         onClick={handleWhatsAppShare}
                         disabled={isSendingEmail || !uploadedFiles.length}
                         className="w-full h-11 bg-green-500 hover:bg-green-600 text-white font-black rounded-lg transition-all flex items-center justify-center gap-2 text-sm disabled:opacity-60"
                       >
                          <span className="material-symbols-outlined text-lg">share</span>
                          WhatsApp
                       </button>
                       <button type="button" onClick={resetForm} className="text-[9px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-600 pt-1 transition-colors">Start Over</button>
                    </div>
                 </form>
              </div>
            )}

            {uploadStep === 'complete' && activeTab === 'send' && emailSentSuccess && (
              <div className="min-h-[320px] md:min-h-[400px] flex flex-col items-center justify-center animate-in zoom-in duration-500 text-center bg-white rounded-3xl p-6 md:p-8 border border-slate-100 shadow-sm">
                 <div className="size-24 bg-green-100 rounded-[2rem] flex items-center justify-center text-green-600 mb-8 shadow-lg">
                    <span className="material-symbols-outlined text-5xl font-bold">mark_email_read</span>
                 </div>
                 <h2 className="text-2xl font-black text-slate-900 mb-2">Email Dispatched!</h2>
                 <p className="text-sm text-slate-500 font-medium px-12 leading-relaxed mb-6">We've sent your transfer link to <b>{emailToChips.join(', ')}</b>. They have 7 days to download the assets.</p>
                 {downloadUrl && (
                   <div className="bg-slate-50 border border-slate-100 rounded-2xl px-4 py-3 text-xs font-bold text-slate-500 mb-8 max-w-xs">
                     Download link ready:
                     <a href={downloadUrl} className="ml-2 text-primary underline break-all">Open</a>
                   </div>
                 )}
                 <button onClick={resetForm} className="px-10 py-4 bg-slate-900 text-white font-black rounded-2xl hover:bg-black transition-all">Send another transfer</button>
              </div>
            )}

            {uploadStep === 'complete' && activeTab === 'store' && (
              <div className="animate-in slide-in-from-bottom-8 duration-500 space-y-4 max-h-[60vh] overflow-y-auto md:max-h-none">
                 <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden max-h-[50vh] md:max-h-none">
                    <div className="absolute top-0 right-0 p-8 opacity-10">
                       <span className="material-symbols-outlined text-9xl">verified_user</span>
                    </div>
                    <div className="relative z-10">
                       <h3 className="text-xl font-black mb-2 flex items-center gap-2"><span className="material-symbols-outlined text-xl">done_all</span> Upload Complete</h3>
                       <p className="text-white/60 text-sm font-medium leading-relaxed mb-6">{uploadedFiles.length} Files uploaded • ENCRYPTED and SAFE</p>
                    </div>
                 </div>

                 <div className="pt-4 flex flex-col gap-3">
                    <button onClick={() => navigate('/drive')} className="w-full h-16 bg-primary hover:bg-blue-600 text-white font-black rounded-2xl shadow-xl shadow-blue-500/20 transition-all flex items-center justify-center gap-2">
                       <span className="material-symbols-outlined">hard_drive</span>
                       Open My Drive
                    </button>
                    <button onClick={resetForm} className="text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-600 pt-2 transition-colors">Done</button>
                 </div>
              </div>
            )}
          </div>
        </div>

        {/* Hero Illustration Content - Text Overlay */}
        <div className="hidden md:flex flex-1 relative items-center justify-center overflow-hidden">
          <div className="z-20 p-12 text-left animate-in fade-in zoom-in duration-1000 absolute bottom-0 left-0">
            <h2 className="font-serif text-white tracking-tight leading-none drop-shadow-lg max-w-2xl">
              <span className="block text-6xl lg:text-8xl font-medium">Your Life.</span>
              <span className="block text-5xl lg:text-6xl font-medium text-slate-300 italic mt-1">Your Memories.</span>
            </h2>
            <p className="text-lg text-slate-300 font-semibold leading-tight mt-4 max-w-lg drop-shadow">
              Protected Today. Remembered Forever.
            </p>
          </div>
        </div>
      </main>
      <MobileBottomNav isLoggedIn={isLoggedIn} />
    </div>
  );
};

export default Landing;
